import React, { useEffect, useImperativeHandle, useState } from "react";
import {
  Row,
  Col,
  Button,
  Modal,
  ModalBody,
  Input,
  Spinner,
  Alert,
} from "reactstrap";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { Icon } from "../../../components/Component";
import FormInput from "../../form-components/InputComponent";
import { Checkbox, Flex, message, Popover, Segmented, Spin, Table } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import FormDatePicker from "../../form-components/DatePicker";
import dayjs from "dayjs";
import { ColumnsType } from "antd/es/table";
import FormTimePicker from "../../form-components/FormTimePickerProps";
import { calendarApis } from "../../../api/Calendar";
import Swal from "sweetalert2";
import { ApiResponse } from "../../../api/master/axiosIndex";
import { LoadingOutlined } from "@ant-design/icons";
import { ReactTyped } from "react-typed";
import { EditAccess } from "../../../components/accessControl/AccessControl";

interface Holiday {
  key: string;
  holidayName: string;
  holidayDate: string;
}
const daysMaster = [
  { dayId: 1, dayName: "Sunday" },
  { dayId: 2, dayName: "Monday" },
  { dayId: 3, dayName: "Tuesday" },
  { dayId: 4, dayName: "Wednesday" },
  { dayId: 5, dayName: "Thursday" },
  { dayId: 6, dayName: "Friday" },
  { dayId: 7, dayName: "Saturday" },
];

const CalendarCreation: React.FC<any> = ({ onSuccess, isOpen }) => {
  const formMethods = useForm({
    mode: "onChange",
  });
  const { watch, register, reset, setValue, handleSubmit, control } =
    formMethods;
  const [sm, setSm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mobileView, setMobileView] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const [warning, setWarning] = useState<{
    error: any | undefined;
    message: string | undefined;
  }>({
    error: undefined,
    message: undefined,
  });
  const [selectedDays, setSelectedDays] = useState<any>([
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
  ]); // Default selection

  const [shifts, setShifts] = useState<
    { name: string; startTime?: string; endTime?: string }[]
  >([{ name: "Day Shift" }, { name: "Night Shift" }]);

  const [selectedShifts, setSelectedShifts] = useState<string[]>([]);
  const [newShiftName, setNewShiftName] = useState("");
  const [popoverVisible, setPopoverVisible] = useState(false);

  const toggleSelection = (day: any) => {
    setSelectedDays((prev: any) =>
      prev.includes(day) ? prev.filter((d: any) => d !== day) : [...prev, day]
    );
  };

  const startDate = watch("startDate");
  const endDate = watch("endDate");

  useEffect(() => {
    if (
      startDate &&
      endDate &&
      dayjs(endDate).isBefore(dayjs(startDate), "day")
    ) {
      setValue("endDate", null);
    }
  }, [startDate]);

  useEffect(() => {
    if (isOpen) {
      reset();
      setHolidays([]);
      setShifts([{ name: "Day Shift" }, { name: "Night Shift" }]);
      setSelectedDays(["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]);
      setSelectedShifts([]);
      setEditingHoliday(null);
      setIsEditMode(false);
      setHolidayName("");
      setDateRange(null);
    }
  }, [isOpen]);

  const toggleShiftSelection = (shiftName: string) => {
    setSelectedShifts((prev) =>
      prev.includes(shiftName)
        ? prev.filter((s) => s !== shiftName)
        : [...prev, shiftName]
    );
  };

  const addNewShift = () => {
    const normalizedNewShift = newShiftName.trim().toLowerCase();

    const isDuplicate = shifts.some(
      (s) => s.name.trim().toLowerCase() === normalizedNewShift
    );

    if (newShiftName.trim() && !isDuplicate) {
      setShifts([...shifts, { name: newShiftName.trim() }]);
      setNewShiftName("");
      setPopoverVisible(false);
    } else if (isDuplicate) {
      message.warning("Shift name already exists!");
    }
  };

  const [modalVisible, setModalVisible] = useState(false);
  const [holidayName, setHolidayName] = useState("");
  const [dateRange, setDateRange] = useState<any>(null);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Handle Save Holiday
  const handleSaveHoliday = () => {
    console.log("data", holidayName, dateRange);
    if (holidayName && dateRange) {
      const normalizedDate = dayjs(dateRange).format("YYYY-MM-DD");

      const otherHolidays =
        isEditMode && editingHoliday
          ? holidays.filter((h) => h.key !== editingHoliday.key)
          : holidays;

      const isDuplicateDate = otherHolidays.some(
        (h) => dayjs(h.holidayDate).format("YYYY-MM-DD") === normalizedDate
      );
      if (isDuplicateDate) {
        message.warning("A holiday already exists on this date!");
        return;
      }

      if (isEditMode && editingHoliday) {
        // Edit existing holiday
        const updatedHolidays = holidays.map((holiday) =>
          holiday.key === editingHoliday.key
            ? { ...holiday, holidayName: holidayName, holidayDate: dateRange }
            : holiday
        );
        setHolidays(updatedHolidays);
      } else {
        // Add new holiday
        const newHoliday: Holiday = {
          key: (holidays.length + 1).toString(),
          holidayName: holidayName,
          holidayDate: dateRange,
        };
        setHolidays([...holidays, newHoliday]);
      }
      setModalVisible(false);
      setHolidayName("");
      setDateRange(null);
      setEditingHoliday(null);
      setIsEditMode(false);
    }
  };

  // Handle Edit Holiday
  const handleEditHoliday = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    setIsEditMode(true);
    setHolidayName(holiday.holidayName);
    setDateRange(holiday.holidayDate);
    setModalVisible(true);
  };

  // Handle Delete Holiday
  const handleDeleteHoliday = (holidayKey: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        const updatedHolidays = holidays.filter(
          (holiday) => holiday.key !== holidayKey
        );
        setHolidays(updatedHolidays);
        Swal.fire("Deleted!", "Holiday has been deleted.", "success");
      }
    });
  };

  // Table Columns
  const columns: ColumnsType<Holiday> = [
    { title: "Holiday Name", dataIndex: "holidayName", key: "holidayName" },
    {
      title: "Holiday Date",
      dataIndex: "holidayDate",
      key: "holidayDate",
      render: (text: any) => {
        return text && dayjs(text).format("DD-MMM-YYYY");
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record: Holiday) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <EditAccess moduleName="Calendar">
            <a
              onClick={() => handleEditHoliday(record)}
              style={{ color: "#2557d6", cursor: "pointer" }}
            >
              <EditOutlined />
            </a>
          </EditAccess>
          <EditAccess moduleName="Calendar">
            <a
              onClick={() => handleDeleteHoliday(record.key)}
              style={{ color: "red", cursor: "pointer" }}
            >
              <DeleteOutlined />
            </a>
          </EditAccess>
        </div>
      ),
    },
  ];

  const transformShifts = (formData: any) => {
    return selectedShifts.map((shiftName) => {
      const startTime = formData[`startTime-${shiftName}`];
      const endTime = formData[`endTime-${shiftName}`];

      if (!startTime || !endTime) {
        throw new Error(
          `Please provide both start and end times for shift: ${shiftName}`
        );
      }

      let shiftType = "Others";
      if (shiftName === "Day Shift") {
        shiftType = "Day";
      } else if (shiftName === "Night Shift") {
        shiftType = "Night";
      }

      return {
        shiftName,
        shiftType,
        shiftStartTime: `${startTime}:00`,
        shiftEndTime: `${endTime}:00`,
      };
    });
  };

  const transformWorkingDays = () => {
    return selectedDays.map((dayName: any) => {
      const match = daysMaster.find((day) => day.dayName === dayName);
      if (!match) {
        throw new Error(`Day ${dayName} not found in days master.`);
      }
      return { dayId: Number(match.dayId) };
    });
  };

  const submitForm = async (data: any) => {
    for (const shiftName of selectedShifts) {
      const startTime = data[`startTime-${shiftName}`];
      const endTime = data[`endTime-${shiftName}`];
      if (startTime && endTime) {
        const start = dayjs(startTime, "HH:mm");
        const end = dayjs(endTime, "HH:mm");
        if (!end.isAfter(start)) {
          formMethods.setError(`endTime-${shiftName}`, {
            type: "manual",
            message: "End Time must be greater than Start Time",
          });
          return;
        }
      }
    }
    setLoading(true);
    setDisableSubmit(true);
    const calendarShifts = transformShifts(data);
    const calendarWorkingDays = transformWorkingDays();
    const finalPayload = {
      calendarTitle: data.calendarTitle,
      calendarStartDate: dayjs(data.startDate).format("YYYY-MM-DD"),
      calendarEndDate: dayjs(data.endDate).format("YYYY-MM-DD"),
      calendarShifts,
      calendarWorkingDays,
      calendarHolidays: holidays.map((holiday) => {
        return {
          holidayName: holiday.holidayName,
          holidayDate: holiday.holidayDate,
        };
      }),
    };
    console.log("finalPayload", finalPayload);
    try {
      const response: ApiResponse = await calendarApis.createCalendar(
        finalPayload
      );
      const { data, status, message, error } = response || {};
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: data?.message,
          confirmButtonText: "Ok",
        });
        onSuccess();
        reset();
        setHolidays([]);
      } else {
        setWarning({
          message,
          error,
        });
        // Swal.fire("Failed!", message, "error");
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Flex align="center" gap="middle" className="pageLoader">
        <Spin
          indicator={<LoadingOutlined spin />}
          className="m-auto"
          size="large"
        />
      </Flex>
    );
  }

  return (
    <>
      <div className="mt-3">
        <div className="nk-wizard nk-wizard-simple is-alter wizard clearfix">
          <Alert
            isOpen={warning?.message != undefined}
            color="danger"
            className="start-0 w-100 py-1 mt-2"
            style={{
              top: 0,
              textAlign: "left",
              fontSize: "14px",
              cursor: "default",
            }}
          >
            <span className="fw-bold">{warning?.error}: </span>
            <ReactTyped
              strings={[warning?.message || ""]}
              typeSpeed={10}
              showCursor={false}
            />
          </Alert>
          <FormProvider {...formMethods}>
            <form
              className="content clearfix"
              onSubmit={handleSubmit(submitForm)}
            >
              <Row className="gy-4">
                <Col lg="4">
                  <FormInput
                    name="calendarTitle"
                    label="Calendar Title"
                    required={true}
                    type="text"
                    placeholder="Enter Calendar Title"
                    // tooltipText="Enter the Calendar Title for the Calendar."
                  />
                </Col>
                <Col lg="4">
                  <FormDatePicker
                    name="startDate"
                    label="Start Date"
                    required={true}
                    // minDate={new Date()}
                  />
                </Col>
                <Col lg="4">
                  <FormDatePicker
                    name="endDate"
                    label="End Date"
                    required={true}
                    disabled={!startDate}
                    minDate={
                      startDate ? dayjs(startDate).toDate() : undefined
                    }
                  />
                </Col>
                {/* <Col lg="8">
                  <label className="form-label">
                    Setup Working Days
                    <span className="mandatory">*</span>
                  </label>
                  <Segmented
                    options={daysMaster.map((day) => ({
                      label: (
                        <span
                          style={{
                            color: selectedDays.includes(day)
                              ? "#f3f3f3"
                              : "black",
                          }}
                        >
                          {day}
                        </span>
                      ),
                      value: day,
                      className: selectedDays.includes(day)
                        ? "selected-segment"
                        : "",
                    }))}
                    onChange={toggleSelection}
                    block
                  />
                </Col> */}
                <Col lg="8">
                  <label className="form-label">
                    Setup Working Days
                    <span className="mandatory">*</span>
                  </label>
                  <Segmented
                    options={daysMaster.map((day) => ({
                      label: (
                        <span
                          style={{
                            color: selectedDays.includes(day.dayName)
                              ? "#f3f3f3"
                              : "black",
                          }}
                        >
                          {day.dayName}
                        </span>
                      ),
                      value: day.dayName,
                      className: selectedDays.includes(day.dayName)
                        ? "selected-segment"
                        : "",
                    }))}
                    value={selectedDays}
                    onChange={toggleSelection}
                    block
                    multiple
                  />
                </Col>
                <Col lg={8}>
                  <Row>
                    <Col>
                      <label className="form-label">Setup Working Hours</label>
                    </Col>
                    <Col lg={2}>
                      <Popover
                        content={
                          <div style={{ display: "flex", gap: "10px" }}>
                            <Input
                              placeholder="Shift Name"
                              value={newShiftName}
                              onChange={(e) => setNewShiftName(e.target.value)}
                            />
                            <EditAccess moduleName="Calendar">
                              <Button
                                // type="primary"
                                onClick={addNewShift}
                              >
                                Save
                              </Button>
                            </EditAccess>
                          </div>
                        }
                        title="Add New Shift"
                        trigger="click"
                        open={popoverVisible}
                        onOpenChange={setPopoverVisible}
                      >
                        <a
                          style={{
                            cursor: "pointer",
                            color: "#1890ff",
                          }}
                        >
                          + Add Shift
                        </a>
                      </Popover>
                    </Col>
                  </Row>

                  {shifts.map((shift) => (
                    <Row
                      key={shift.name}
                      // align="middle"
                      style={{ marginBottom: "10px" }}
                    >
                      {/* Align checkbox to the left */}
                      <Col span={6}>
                        <Checkbox
                          checked={selectedShifts.includes(shift.name)}
                          onChange={() => toggleShiftSelection(shift.name)}
                        >
                          {shift.name}
                        </Checkbox>
                      </Col>

                      {/* Show Start & End Time Pickers when checkbox is selected */}
                      {selectedShifts.includes(shift.name) && (
                        <>
                          <Col span={8}>
                            <FormTimePicker
                              name={`startTime-${shift.name}`}
                              label={""}
                              placeholder="Start Time"
                              required={true}
                              onChange={() => {
                                const currentEnd = watch(
                                  `endTime-${shift.name}`
                                );
                                if (currentEnd) {
                                  setValue(`endTime-${shift.name}`, "");
                                }
                              }}
                            />
                          </Col>
                          <Col span={8}>
                            <FormTimePicker
                              name={`endTime-${shift.name}`}
                              label={""}
                              placeholder="End Time"
                              required={true}
                              disabled={!watch(`startTime-${shift.name}`)}
                            />
                          </Col>
                        </>
                      )}
                    </Row>
                  ))}
                </Col>
              </Row>
              <div>
                {/* Label with "Add Holiday" Button */}
                <Row
                  justify="space-between"
                  // align="middle"
                  style={{ marginTop: "15px" }}
                >
                  <Col>
                    <label className="form-label">Setup Holidays</label>
                  </Col>
                  <Col style={{ justifyContent: "end", display: "flex" }}>
                    <a
                      style={{
                        cursor: "pointer",
                        color: "#1890ff",
                      }}
                      onClick={() => {
                        setModalVisible(true);
                        setDateRange("");
                        setValue("holidayName", "");
                        setValue("holidayDate", "");
                        setHolidayName("");
                        setEditingHoliday(null);
                        setIsEditMode(false);
                      }}
                    >
                      + Add Holiday
                    </a>
                  </Col>
                </Row>

                {/* Ant Design Table */}
                <Table
                  dataSource={holidays}
                  columns={columns}
                  pagination={false}
                  bordered
                />

                {/* Add/Edit Holiday Modal */}
                <Modal
                  title={isEditMode ? "Edit Holiday" : "Add Holiday"}
                  isOpen={modalVisible}
                  className="modal-dialog-centered"
                  toggle={() => {
                    setModalVisible(false);
                    setEditingHoliday(null);
                    setIsEditMode(false);
                    setHolidayName("");
                    setDateRange(null);
                  }}
                  backdrop="static"
                  // onOk={handleSaveHoliday}
                >
                  <a
                    href="#dropdownitem"
                    onClick={(ev) => {
                      ev.preventDefault();
                      setModalVisible(false);
                      setEditingHoliday(null);
                      setIsEditMode(false);
                      setHolidayName("");
                      setDateRange(null);
                    }}
                    className="close"
                  >
                    <Icon name="cross-sm"></Icon>
                  </a>
                  <ModalBody>
                    <div className="nk-modal-head mb-3">
                      <h5 className="title">
                        {isEditMode ? "Edit Holiday" : "Set Holiday"}
                      </h5>
                    </div>
                    {/* <form onSubmit={handleSubmit(onSubmitHoliday)}> */}
                    <div style={{ marginBottom: "15px" }}>
                      <FormInput
                        name="holidayName"
                        label="Holiday Name"
                        required={true}
                        type="text"
                        placeholder="Enter Holiday Name"
                        onInput={(value: any) => {
                          // console.log("value", value.target.value);
                          setHolidayName(value.target.value);
                        }}
                      />
                    </div>
                    <div>
                      <FormDatePicker
                        name="holidayDate"
                        label="Holiday Date"
                        required={true}
                        placeholder="Select Holiday Date"
                        onChange={(value: any) => {
                          setDateRange(dayjs(value).format("YYYY-MM-DD"));
                        }}
                      />
                    </div>
                    <Row className="gy-4 mt-2">
                      <Col size="12">
                        <ul className="align-center flex-wrap flex-sm-nowrap gx-4 gy-2">
                          <li>
                            <EditAccess moduleName="Calendar">
                              <Button
                                color="primary"
                                size="lg"
                                onClick={() => handleSaveHoliday()}
                                type="submit"
                              >
                                Save
                              </Button>
                            </EditAccess>
                          </li>
                          <li>
                            <a
                              href="#dropdownitem"
                              onClick={(ev) => {
                                ev.preventDefault();
                                setModalVisible(false);
                                setEditingHoliday(null);
                                setIsEditMode(false);
                                setHolidayName("");
                                setDateRange(null);
                              }}
                              className="link link-light"
                            >
                              Cancel
                            </a>
                          </li>
                        </ul>
                      </Col>
                    </Row>
                    {/* </form> */}
                  </ModalBody>
                </Modal>
              </div>
              <Row className="gy-4 mt-1">
                <Col>
                  {disableSubmit ? (
                    <Button color="primary" type="button">
                      <Spinner size="sm" color="light" />
                    </Button>
                  ) : (
                    <EditAccess moduleName="Calendar">
                      <Button color="primary" type="submit">
                        Submit
                      </Button>
                    </EditAccess>
                  )}
                </Col>
              </Row>
            </form>
          </FormProvider>
        </div>
      </div>
    </>
  );
};

export default CalendarCreation;
